import { type CSSProperties, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { Button } from '../buttons/Button';
import { GoogleFontIcon } from '../icons/GoogleFontIcon';
import { SVGIcon } from '../icons/SVGIcon';

import { usePageSidebarHost } from './PageSidebarHost';
import { PageSidebarProps } from './types';
import { usePinnedSlideOpen } from './usePinnedSlideOpen';
import {
  clampPanelWidth,
  getDefaultPanelWidth,
  useResizablePanelWidth,
} from './useResizablePanelWidth';

import styles from './PageSidebar.module.scss';

function resolveAriaLabel(
  ariaLabel: string | undefined,
  title: PageSidebarProps['title']
): string | undefined {
  if (ariaLabel != null) {
    return ariaLabel;
  }

  return typeof title === 'string' ? title : undefined;
}

export function PageSidebar({
  ariaLabel,
  children,
  fillRemaining = false,
  headerStart,
  fullscreen = false,
  onClose,
  onPinnedChange,
  onSidebarWidthChange,
  open,
  pinningEnabled = true,
  pinned,
  resizable = true,
  sidebarWidth: sidebarWidthProp,
  title,
}: PageSidebarProps) {
  const { t } = useTranslation();
  const hostContext = usePageSidebarHost();
  const setReservePinnedContentSpace =
    hostContext?.setReservePinnedContentSpace;

  const { isResizing, panelWidth, setPanelWidth, startResize } =
    useResizablePanelWidth({
      enabled: resizable && !fillRemaining,
      initialWidth:
        sidebarWidthProp ?? hostContext?.sidebarWidth ?? getDefaultPanelWidth(),
      onWidthChange: onSidebarWidthChange,
    });

  const handlePinnedCloseComplete = useCallback(() => {
    setReservePinnedContentSpace?.(false);
  }, [setReservePinnedContentSpace]);

  const { handleTransitionEnd, isAccessibilityVisible, isVisuallyOpen } =
    usePinnedSlideOpen({
      onPinnedCloseComplete: handlePinnedCloseComplete,
      open,
      pinned,
    });

  useEffect(() => {
    const externalWidth = sidebarWidthProp ?? hostContext?.sidebarWidth;

    if (externalWidth != null) {
      setPanelWidth(externalWidth);
    }
  }, [hostContext?.sidebarWidth, sidebarWidthProp, setPanelWidth]);

  const handlePinToggle = useCallback(() => {
    onPinnedChange(!pinned);
  }, [onPinnedChange, pinned]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const reservePinnedSpace =
    hostContext?.reservePinnedContentSpace ??
    (pinned && isAccessibilityVisible);

  return (
    <aside
      aria-hidden={!isAccessibilityVisible}
      aria-label={resolveAriaLabel(ariaLabel, title)}
      className={clsx(styles['root'], {
        [styles['root--open']]: isVisuallyOpen,
        [styles['root--pinned']]: pinned,
        [styles['root--fill-remaining']]: fillRemaining,
        [styles['root--fullscreen']]: fullscreen,
        [styles['root--resizing']]: isResizing,
        [styles['root--space-reserved']]: reservePinnedSpace,
      })}
      data-testid="page-sidebar"
      onTransitionEnd={handleTransitionEnd}
      role="dialog"
      style={
        {
          '--page-sidebar-width': `${clampPanelWidth(panelWidth)}px`,
        } as CSSProperties
      }
    >
      {resizable && !fillRemaining ? (
        <div
          aria-hidden="true"
          className={styles['resize-handle']}
          onPointerDown={startResize}
          data-testid="page-sidebar-resize-handle"
        />
      ) : null}

      <header
        className={styles['panel-header']}
        data-testid="page-sidebar-header"
      >
        <div className={styles['panel-header-left']}>
          {headerStart}
          <span
            className={styles['panel-title']}
            data-testid="page-sidebar-title"
          >
            {title}
          </span>
        </div>
        <div className={styles['panel-actions']}>
          {pinningEnabled ? (
            <Button
              icon={
                <GoogleFontIcon name={pinned ? 'keep_off' : 'keep'} solid />
              }
              onClick={handlePinToggle}
              title={
                pinned
                  ? t('app.pageSidebar.unpinSidebar')
                  : t('app.pageSidebar.pinSidebar')
              }
              variant="text"
              data-testid="page-sidebar-pin"
            />
          ) : null}
          <Button
            icon={<SVGIcon name="close" />}
            onClick={handleClose}
            title={t('app.pageSidebar.closeSidebar')}
            variant="text"
            data-testid="page-sidebar-close"
          />
        </div>
      </header>

      <div className={styles['content']}>{children}</div>
    </aside>
  );
}
